// Accounting — Settlements
//
// Tracks what actually reached the bank:
//   • online provider clearing (ToyyibPay / Billplz) — collected from the
//     payment ledger (net of the provider fee) vs payouts recorded here
//   • card / other clearing buckets — collected from sales receipts
//   • cash -> bank deposits, opening balances and adjustments
// Admin-only for writes; the gateway enforces the same rule.

import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { api } from '../lib/api.js';
import type {
  AccountingMeta,
  AccountingPayBy,
  SettlementsResult,
  SettlementTransfer,
} from '../lib/api.js';
import { OutletViewCache } from '../lib/outlet-view-cache.js';
import { TAB_UI_STYLES } from '../styles/tab-ui.js';

const money = (n: number | undefined) => (Number(n) || 0).toFixed(2);

function todayIso(): string {
  const d = new Date();
  const z = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return z.toISOString().slice(0, 10);
}
function isoToMs(v: string, endOfDay = false): number {
  if (!v) return 0;
  const d = new Date(`${v}T00:00:00`);
  if (endOfDay) d.setHours(23, 59, 59, 999);
  return d.getTime();
}
function msToIso(ms: number): string {
  if (!ms) return todayIso();
  const d = new Date(ms);
  const z = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return z.toISOString().slice(0, 10);
}

type SettlementKind = 'payout' | 'deposit' | 'opening' | 'adjustment';

interface Form {
  kind: SettlementKind;
  date: number;
  fromAccountId: number;
  toAccountId: number;
  amount: number;
  feeAmount: number;
  paidById: string;
  provider: string;
  reference: string;
  remark: string;
}

@customElement('settlements-panel')
export class SettlementsPanel extends LitElement {
  static styles = [TAB_UI_STYLES, css`
    :host { display: flex; flex-direction: column; height: 100%; min-height: 0; overflow: hidden; }
    .bar { display: flex; flex-wrap: wrap; gap: 0.5rem; align-items: end; margin: 0.75rem 1rem 0.5rem; flex-shrink: 0; }
    .bar .fg { margin: 0; }
    .bar input { min-height: 38px; padding: 7px 10px; border: 1px solid var(--color-border); border-radius: 10px; background: var(--color-surface); color: var(--color-text); font-family: inherit; font-size: 0.84rem; }
    .body { flex: 1; min-height: 0; overflow: auto; padding: 0 1rem 1rem; }
    .cards { display: grid; grid-template-columns: repeat(auto-fill, minmax(250px, 1fr)); gap: 0.6rem; margin-bottom: 1rem; }
    .card { border: 1px solid var(--color-border); border-radius: 14px; background: var(--color-surface); padding: 0.8rem 0.9rem; }
    .card h4 { margin: 0 0 0.5rem; font-size: 0.86rem; display: flex; justify-content: space-between; align-items: center; }
    .card .line { display: flex; justify-content: space-between; font-size: 0.8rem; padding: 2px 0; }
    .card .line.total { border-top: 1px dashed var(--color-border); margin-top: 0.35rem; padding-top: 0.4rem; font-weight: 700; }
    .wrap { border: 1px solid var(--color-border); border-radius: 12px; overflow: auto; background: var(--color-surface); margin-bottom: 1rem; }
    .state { padding: 2rem; text-align: center; color: var(--color-text-secondary); }
    .acct .id { margin-left: 0.3rem; font-size: 0.72rem; color: var(--color-text-secondary); }
    .error { margin: 0 1rem 0.5rem; padding: 0.6rem 0.8rem; border-radius: 10px; background: #fdecea; color: #b71c1c; font-size: 0.84rem; }
    .modal-overlay { position: fixed; inset: 0; background: rgba(15, 23, 42, 0.45); display: none; align-items: center; justify-content: center; z-index: 60; padding: 1rem; }
    .modal-overlay.open { display: flex; }
    .modal { width: min(620px, 100%); max-height: 90vh; overflow: auto; background: var(--color-surface); border-radius: 16px; }
    .modal-header { display: flex; align-items: center; justify-content: space-between; padding: 0.9rem 1.1rem; border-bottom: 1px solid var(--color-border); }
    .modal-header h3 { margin: 0; font-size: 1rem; }
    .modal-body { padding: 1rem 1.1rem; }
    .modal-footer { display: flex; justify-content: flex-end; gap: 0.5rem; padding: 0.8rem 1.1rem; border-top: 1px solid var(--color-border); }
    .close { border: none; background: none; font-size: 1.35rem; cursor: pointer; color: var(--color-text-secondary); }
    .hint { font-size: 0.75rem; color: var(--color-text-secondary); margin-top: 0.25rem; }
  `];

  @property({ type: String }) storeCode = '';
  @property({ type: Boolean }) isAdmin = false;

  @state() private _meta: AccountingMeta | null = null;
  @state() private _data: SettlementsResult | null = null;
  @state() private _from = msToIso(new Date(new Date().getFullYear(), new Date().getMonth(), 1).getTime());
  @state() private _to = todayIso();
  @state() private _loading = false;
  @state() private _error = '';
  @state() private _form: Form | null = null;
  @state() private _saving = false;
  @state() private _formError = '';

  private _view = new OutletViewCache<{ from: string; to: string }>(this, 'accounting-settlements', {
    capture: () => ({ from: this._from, to: this._to }),
    restore: (v) => { this._from = v.from; this._to = v.to; },
    reset: () => {
      this._from = msToIso(new Date(new Date().getFullYear(), new Date().getMonth(), 1).getTime());
      this._to = todayIso();
    },
  });

  connectedCallback() {
    super.connectedCallback();
    // No loads here: updated() drives them from the switchTo() result, so a
    // load started now would be duplicated on the first update.
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    this._view.dispose();
  }
  protected updated(changed: Map<PropertyKey, unknown>) {
    // switchTo() returns 'unchanged' | 'restored' | 'fresh' — compare it, never
    // treat it as a boolean: every one of those strings is truthy, and a
    // truthy check here re-runs the loaders on every render (endless loop).
    const switched = this._view.switchTo(this.storeCode);
    if (switched === 'unchanged' && !changed.has('storeCode')) return;
    this._meta = null;
    // Clearing data is not part of the kept snapshot — always refetch it for
    // the outlet being entered.
    this._data = null;
    void this._loadMeta();
    void this._load();
  }

  private async _loadMeta() {
    if (!this.storeCode) return;
    const epoch = this._view.epoch;
    try {
      const res = await api.accountingMeta(this.storeCode);
      if (this._view.isCurrent(epoch)) this._meta = res.data;
    } catch { /* surfaced by _load */ }
  }

  private async _load() {
    if (!this.storeCode) return;
    const epoch = this._view.epoch;
    this._loading = true;
    this._error = '';
    try {
      const res = await api.accountingSettlements(this.storeCode, {
        from: isoToMs(this._from, false),
        to: isoToMs(this._to, true),
      });
      if (!this._view.isCurrent(epoch)) return;
      this._data = res.data;
    } catch (e: any) {
      if (this._view.isCurrent(epoch)) this._error = e?.message || 'Failed to load settlements';
    } finally {
      if (this._view.isCurrent(epoch)) this._loading = false;
    }
  }

  private _bucketPayBys(): AccountingPayBy[] {
    return (this._meta?.payBy ?? []).filter((p) => p.kind === 'card' || p.kind === 'clearing_online');
  }

  private _openForm(kind: SettlementKind) {
    const meta = this._meta;
    const bank = meta?.accounts.find((a) => /current a\/c/i.test(a.name))?.id ?? '';
    this._form = {
      kind,
      date: isoToMs(todayIso(), false),
      fromAccountId: 0,
      toAccountId: Number(bank) || 0,
      amount: 0,
      feeAmount: 0,
      paidById: '',
      provider: '',
      reference: '',
      remark: '',
    };
    this._formError = '';
  }

  private _set<K extends keyof Form>(key: K, value: Form[K]) {
    if (!this._form) return;
    this._form = { ...this._form, [key]: value };
  }

  private _pickBucket(id: string) {
    if (!this._form) return;
    const pb = this._bucketPayBys().find((p) => p.id === id);
    const provider = pb && /toyyibpay/i.test(pb.name) ? 'toyyibpay' : pb && /billplz/i.test(pb.name) ? 'billplz' : '';
    this._form = {
      ...this._form,
      paidById: id,
      provider,
      fromAccountId: pb?.accountEntityId || this._form.fromAccountId,
    };
  }

  private async _save() {
    const f = this._form;
    if (!f) return;
    if (!f.date) { this._formError = 'Date is required.'; return; }
    if (!(f.amount > 0)) { this._formError = 'Amount must be greater than zero.'; return; }
    if (!f.toAccountId) { this._formError = 'Target account is required.'; return; }
    if (f.kind !== 'opening' && !f.fromAccountId) { this._formError = 'Source account is required.'; return; }
    if (f.fromAccountId && f.fromAccountId === f.toAccountId) { this._formError = 'Source and target must differ.'; return; }
    this._saving = true;
    this._formError = '';
    const epoch = this._view.epoch;
    try {
      await api.accountingSettlementSave(this.storeCode, {
        kind: f.kind,
        date: f.date,
        fromAccountId: f.fromAccountId || undefined,
        toAccountId: f.toAccountId,
        amount: Number(f.amount) || 0,
        feeAmount: Number(f.feeAmount) || 0,
        paidById: f.paidById || undefined,
        provider: f.provider || undefined,
        reference: f.reference || undefined,
        remark: f.remark || undefined,
      });
      if (!this._view.isCurrent(epoch)) return;
      this._form = null;
      await this._load();
    } catch (e: any) {
      if (this._view.isCurrent(epoch)) this._formError = e?.message || 'Save failed';
    } finally {
      if (this._view.isCurrent(epoch)) this._saving = false;
    }
  }

  /** Account cell: name first (resolved by the gateway), id as a muted suffix. */
  private _account(id: number, name: string) {
    if (!id) return '—';
    return html`<span class="acct">${name || 'Unknown account'}<span class="id">#${id}</span></span>`;
  }

  private async _delete(t: SettlementTransfer) {
    if (!window.confirm(`Delete this ${t.kind} of RM ${money(t.amount)}?`)) return;
    const epoch = this._view.epoch;
    try {
      await api.accountingSettlementDelete(this.storeCode, t.id);
      if (!this._view.isCurrent(epoch)) return;
      await this._load();
    } catch (e: any) {
      if (this._view.isCurrent(epoch)) this._error = e?.message || 'Delete failed';
    }
  }

  private _renderForm() {
    const f = this._form!;
    const accounts = this._meta?.accounts ?? [];
    return html`
      <div class="modal-overlay open" @click=${(ev: Event) => { if (ev.target === ev.currentTarget) this._form = null; }}>
        <div class="modal" @click=${(ev: Event) => ev.stopPropagation()}>
          <div class="modal-header">
            <h3>${f.kind === 'payout' ? 'Record payout' : f.kind === 'deposit' ? 'Cash bank deposit'
              : f.kind === 'opening' ? 'Opening balance' : 'Adjustment'}</h3>
            <button class="close" @click=${() => (this._form = null)}>&times;</button>
          </div>
          <div class="modal-body">
            ${this._formError ? html`<div class="error" style="margin:0 0 0.7rem">${this._formError}</div>` : ''}
            <div class="grid">
              <div class="fg">
                <label>Date</label>
                <input type="date" .value=${msToIso(f.date)}
                  @change=${(ev: Event) => this._set('date', isoToMs((ev.target as HTMLInputElement).value))}>
              </div>
              ${f.kind === 'payout' || f.kind === 'adjustment' ? html`
                <div class="fg">
                  <label>Bucket (pay-by)</label>
                  <select .value=${f.paidById} @change=${(ev: Event) => this._pickBucket((ev.target as HTMLSelectElement).value)}>
                    <option value="">Select…</option>
                    ${this._bucketPayBys().map((p) => html`<option value=${p.id} ?selected=${f.paidById === p.id}>${p.name}</option>`)}
                  </select>
                </div>` : ''}
              ${f.kind !== 'opening' ? html`
                <div class="fg">
                  <label>From account</label>
                  <select .value=${String(f.fromAccountId)}
                    @change=${(ev: Event) => this._set('fromAccountId', Number((ev.target as HTMLSelectElement).value))}>
                    <option value="0">Select…</option>
                    ${accounts.map((a) => html`<option value=${a.id} ?selected=${String(f.fromAccountId) === a.id}>${a.name}</option>`)}
                  </select>
                </div>` : ''}
              <div class="fg">
                <label>To account</label>
                <select .value=${String(f.toAccountId)}
                  @change=${(ev: Event) => this._set('toAccountId', Number((ev.target as HTMLSelectElement).value))}>
                  <option value="0">Select…</option>
                  ${accounts.map((a) => html`<option value=${a.id} ?selected=${String(f.toAccountId) === a.id}>${a.name}</option>`)}
                </select>
              </div>
              <div class="fg">
                <label>Amount credited (RM)</label>
                <input type="number" step="0.01" min="0" .value=${String(f.amount)}
                  @input=${(ev: Event) => this._set('amount', Number((ev.target as HTMLInputElement).value))}>
              </div>
              <div class="fg">
                <label>Fee absorbed (RM)</label>
                <input type="number" step="0.01" min="0" .value=${String(f.feeAmount)}
                  @input=${(ev: Event) => this._set('feeAmount', Number((ev.target as HTMLInputElement).value))}>
                <div class="hint">Bank / provider charge deducted from the source account.</div>
              </div>
              <div class="fg">
                <label>Reference</label>
                <input .value=${f.reference} @input=${(ev: Event) => this._set('reference', (ev.target as HTMLInputElement).value)}>
              </div>
              <div class="fg full">
                <label>Remark</label>
                <textarea .value=${f.remark} @input=${(ev: Event) => this._set('remark', (ev.target as HTMLTextAreaElement).value)}></textarea>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button class="btn-cancel" @click=${() => (this._form = null)}>Cancel</button>
            <button class="btn-submit" ?disabled=${this._saving} @click=${() => void this._save()}>
              ${this._saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    `;
  }

  render() {
    if (!this.storeCode) return html`<div class="state">No outlet selected.</div>`;
    const d = this._data;
    const bankedTotal = (provider: string) =>
      (d?.banked ?? []).filter((b) => (b.provider || '') === provider).reduce((s, b) => s + b.amount, 0);

    return html`
      <div class="bar">
        <div class="fg"><label>From</label>
          <input type="date" .value=${this._from} @change=${(ev: Event) => { this._from = (ev.target as HTMLInputElement).value; void this._load(); }}></div>
        <div class="fg"><label>To</label>
          <input type="date" .value=${this._to} @change=${(ev: Event) => { this._to = (ev.target as HTMLInputElement).value; void this._load(); }}></div>
        <button class="btn" @click=${() => void this._load()} ?disabled=${this._loading}>↻ Refresh</button>
        ${this.isAdmin ? html`
          <button class="btn" @click=${() => this._openForm('payout')}>💳 Record payout</button>
          <button class="btn-sec btn" @click=${() => this._openForm('deposit')}>🏦 Cash deposit</button>
          <button class="btn-sec btn" @click=${() => this._openForm('opening')}>📌 Opening balance</button>
          <button class="btn-sec btn" @click=${() => this._openForm('adjustment')}>⚙ Adjustment</button>
        ` : ''}
      </div>
      ${this._error ? html`<div class="error">${this._error}</div>` : ''}

      <div class="body">
        <div class="cards">
          ${(d?.online ?? []).map((o) => {
            const banked = bankedTotal(o.provider);
            return html`<div class="card">
              <h4>${o.provider === 'billplz' ? 'Billplz' : 'ToyyibPay'} clearing <span class="badge">${o.entries}</span></h4>
              <div class="line"><span>Collected</span><span>${money(o.collected)}</span></div>
              <div class="line"><span>Provider fee</span><span>${money(o.fee)}</span></div>
              <div class="line"><span>Expected net</span><span>${money(o.net)}</span></div>
              <div class="line"><span>Banked</span><span>${money(banked)}</span></div>
              <div class="line total"><span>In transit</span><span>${money(o.net - banked)}</span></div>
            </div>`;
          })}
          ${(d?.collected ?? []).map((c) => {
            const banked = (d?.banked ?? []).filter((b) => b.paidById === c.paidById).reduce((s, b) => s + b.amount, 0);
            return html`<div class="card">
              <h4>${c.name} clearing <span class="badge">${c.entries}</span></h4>
              <div class="line"><span>Collected</span><span>${money(c.amount)}</span></div>
              <div class="line"><span>Banked</span><span>${money(banked)}</span></div>
              <div class="line total"><span>In transit</span><span>${money(c.amount - banked)}</span></div>
            </div>`;
          })}
          ${d && !(d.online ?? []).length && !(d.collected ?? []).length
            ? html`<div class="state">No clearing activity in this period.</div>` : ''}
        </div>

        <h4 style="margin:0 0 0.5rem;font-size:0.86rem">Recorded movements</h4>
        <div class="wrap">
          <table>
            <thead><tr><th>Date</th><th>Kind</th><th>From</th><th>To</th>
              <th class="num">Amount</th><th class="num">Fee</th><th>Reference</th><th>By</th><th></th></tr></thead>
            <tbody>
              ${(d?.transfers ?? []).map((t) => html`<tr>
                <td>${msToIso(t.date)}</td>
                <td>${t.kind}</td>
                <td>${this._account(t.fromAccountId, t.fromAccountName)}</td>
                <td>${this._account(t.toAccountId, t.toAccountName)}</td>
                <td class="num">${money(t.amount)}</td>
                <td class="num">${t.feeAmount ? money(t.feeAmount) : '—'}</td>
                <td>${t.reference || t.remark || '—'}</td>
                <td>${t.createdBy || '—'}</td>
                <td>${this.isAdmin ? html`<button class="btn-sm" style="color:var(--color-error,#d93025)" @click=${() => void this._delete(t)}>Delete</button>` : ''}</td>
              </tr>`)}
              ${!(d?.transfers ?? []).length ? html`<tr><td colspan="9" class="state">Nothing recorded yet.</td></tr>` : ''}
            </tbody>
          </table>
        </div>
        <div class="hint">Account ids refer to the Chart of Accounts mapping (⚙ Settings in the Accounting header).</div>
      </div>
      ${this._form ? this._renderForm() : ''}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'settlements-panel': SettlementsPanel;
  }
}
